import { useContext } from '@nuxtjs/composition-api';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import 'dayjs/locale/en';
import 'dayjs/locale/ru';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { LANG, DATE_FORMAT } from '../../../constants/config.js';

dayjs.locale(LANG);
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Get Date And Time
 * @param {string} dateString date
 * @example 03 Kasım, 15:49
 */
export const getDateWithTime = (dateString) => {
  if (!dateString || !dayjs().isValid(dateString)) return null;
  const difference = dayjs().year() - dayjs(dateString).year();
  const format = difference > 0 ? DATE_FORMAT.withoutTime : DATE_FORMAT.withoutYear;
  return dayjs.utc(dateString).tz('Europe/Istanbul').format(format);
};

/**
 * Get Date And Time
 * @param {string} dateString date
 * @example 11.11.20 - 00:31
 */
export const articleDateFormat = (dateString) => {
  if (!dateString || !dayjs().isValid(dateString)) return null;
  return dayjs.utc(dateString).tz('Europe/Istanbul').format(DATE_FORMAT.default);
};

/**
 * Get Date
 * @param {string} dateString date
 * @example 11.11.20
 */
export const onlyDateFormat = (dateString) => {
  if (!dateString || !dayjs().isValid(dateString)) return null;
  return dayjs.utc(dateString).tz('Europe/Istanbul').format('DD.MM.YYYY');
};

/**
 * Get Date For Data Layer
 * @param {string} dateString date
 */
export const eventDateFormat = (dateString) => {
  if (!dateString || !dayjs().isValid(dateString)) return null;
  return dayjs.utc(dateString).tz('Europe/Istanbul').format('YYYY-MM-DD');
};

/**
 * Get Date For Data Layer
 * @param {string} dateString date
 * @param {string} dateCompareString date
 */
export const isSameDate = (dateString, dateCompareString) => {
  return dayjs.utc(dateString).tz('Europe/Istanbul').isSame(dayjs.utc(dateCompareString).tz('Europe/Istanbul'), 'minute');
};

/**
 * Get Day Numbers
 */
export const dayNumbers = () => {
  const { i18n } = useContext();
  const day = i18n.t('main.time.day');
  const days = [day];
  for (let i = 1; i <= 31; i++) {
    days.push(i.toString());
  }
  return days;
};

/**
 * Get Month Numbers
 */
export const monthNumbers = () => {
  const { i18n } = useContext();
  const month = i18n.t('main.time.month');
  const months = [month];
  for (let i = 1; i <= 12; i++) {
    months.push(i.toString());
  }
  return months;
};

/**
 * Get Year Numbers
 */
export const yearNumbers = () => {
  const { i18n } = useContext();
  const year = i18n.t('main.time.year');
  const years = [year];
  const currentYear = Number(dayjs(new Date()).format('YYYY'));
  for (let i = currentYear; i >= currentYear - 100; i--) {
    years.push(i.toString());
  }
  return years;
};

/**
 * Get day of date
 * @param {string} dateString date
 * @example 5
 */
export const getDayOfDate = (date) => {
  if (!date) {
    const { i18n } = useContext();
    return i18n.t('main.time.day');
  }
  return Number(dayjs(date).format('D'));
};

/**
 * Get month of date
 * @param {string} dateString date
 * @example 8
 */
export const getMonthOfDate = (date) => {
  if (!date) {
    const { i18n } = useContext();
    return i18n.t('main.time.month');
  }
  return Number(dayjs(date).format('M'));
};

/**
 * Get year of date
 * @param {string} dateString date
 * @example 1991
 */
export const getYearOfDate = (date) => {
  if (!date) {
    const { i18n } = useContext();
    return i18n.t('main.time.year');
  }
  return Number(dayjs(date).format('YYYY'));
};

/**
 * Set date by day, month, year
 * @param {object} params object
 * @param {string} params.day day
 * @param {string} params.month month
 * @param {string} params.year year
 * @example 1991-08-04T21:00:00.000Z
 */
export const setCustomDate = ({ day, month, year }) => {
  const date = dayjs()
    .month(parseInt(month) - 1)
    .date(parseInt(day))
    .year(year)
    .format();
  return date;
};

export { dayjs };

/**
 * Get minutes:seconds from duration
 * @param {number} duration number
 * @example 199
 */
export const getFormattedTime = (durationInSeconds) => {
  if (!durationInSeconds) return null;
  const minutes = Math.floor(durationInSeconds / 60);
  const seconds = durationInSeconds % 60;

  const minutesStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
  const secondsStr = seconds < 10 ? `0${seconds}` : `${seconds}`;

  return `${minutesStr}:${secondsStr}`;
};

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = MS_PER_SECOND * 60;
const MS_PER_HOUR = MS_PER_MINUTE * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;
const MS_PER_WEEK = MS_PER_DAY * 7;
/**
 * Time difference text
 *
 * @example timeDifference(date) => 5 gün önce
 *
 * @param {Number} previous
 * @param {Number} [current] optional
 */
export function timeDifference(previous, current) {
  current = current ? dayjs(current).valueOf() : dayjs().valueOf();
  previous = dayjs(previous).valueOf();

  const elapsed = current - previous;

  if (elapsed < MS_PER_HOUR) {
    const now = Math.round(elapsed / MS_PER_MINUTE);
    if (now <= 1) {
      return '1 dk önce'; // 1 minute ago
    } else {
      return `${now} dk önce`; // minutes ago (4 minutes ago)
    }
  } else if (elapsed < MS_PER_DAY) {
    const now = Math.round(elapsed / MS_PER_HOUR);
    if (now === 1) {
      return '1 saat önce'; // 1 hour ago
    } else {
      return `${now} saat önce`; // hours ago (3 hours ago)
    }
  } else if (elapsed < MS_PER_WEEK) {
    const now = Math.round(elapsed / MS_PER_DAY);
    if (now === 1) {
      return 'Dün'; // Yesterday
    } else {
      return `${now} gün önce`;
    }
  } else if (dayjs(current).format('YYYY') <= dayjs(previous).format('YYYY')) {
    return dayjs(previous).format('D MMMM');
  } else {
    return dayjs(previous).format('D MMMM YYYY');
  }
}
